/**
 * TrialStartCard Component
 *
 * Passive start-page card that replaces the boot-time trial dialog on the
 * install day (see isTrialOnboardingDue): it reassures that Personal mode is
 * free, and offers the Pro trial / sign-in as a low-pressure secondary action.
 * Clicking either action routes through trialPromptStore so the dialog logic
 * stays owned by useTrialOnboarding (mounted in App).
 *
 * Renders only while the trial decision is still open (state 'not-started').
 * Once the user starts or skips the trial the card disappears for good; the
 * "Maybe later" action instead snoozes it for two weeks (see dismissTrialCard).
 */

import { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useLicenseStore } from '../store/licenseStore';
import { useTrialPromptStore } from '../store/trialPromptStore';
import { useHasProAccess } from '../hooks/useHasProAccess';
import { isBrowserMode } from '../../../features/browser-mode/browserModeUtils';

// Module-level guard so `ui:trial_card_shown` fires at most once per app
// session, even if the card unmounts/remounts (e.g. switching tabs).
let hasTrackedShownThisSession = false;

export function TrialStartCard() {
  const { track } = useAnalytics();
  const showTrialCard = useLicenseStore((state) => state.shouldShowTrialCard());
  const isInitialized = useLicenseStore((state) => state.isInitialized);
  const hasProAccess = useHasProAccess();

  const isTestMode =
    typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.() === true;
  const allowInTests =
    typeof window !== 'undefined' &&
    window.localStorage.getItem('sql-studio-e2e-allow-trial-onboarding') === '1';

  const isVisible =
    isInitialized &&
    !isBrowserMode() &&
    !hasProAccess &&
    showTrialCard &&
    (!isTestMode || allowInTests);

  useEffect(() => {
    if (!isVisible || hasTrackedShownThisSession) return;
    hasTrackedShownThisSession = true;
    track('ui:trial_card_shown', {});
  }, [isVisible, track]);

  if (!isVisible) return null;

  const handleStartTrial = () => {
    track('ui:trial_card_click', { action: 'start_trial' });
    useTrialPromptStore.getState().request('trial-dialog');
  };

  const handleSignIn = () => {
    track('ui:trial_card_click', { action: 'sign_in' });
    useTrialPromptStore.getState().request('sign-in');
  };

  const handleMaybeLater = () => {
    track('ui:trial_card_click', { action: 'maybe_later' });
    useLicenseStore.getState().dismissTrialCard();
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3"
      data-testid="trial-start-card"
    >
      <div className="flex items-center gap-3">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <p className="text-sm">
          <span className="font-medium">Free for personal use — no account needed.</span>{' '}
          <span className="text-muted-foreground">
            Using it at work? Pro covers commercial use and lifts the size caps — free 14-day
            trial.
          </span>
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={handleMaybeLater}
          data-testid="trial-start-card-maybe-later"
        >
          Maybe later
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignIn}
          data-testid="trial-start-card-sign-in"
        >
          Have Pro? Sign in
        </Button>
        <Button size="sm" onClick={handleStartTrial} data-testid="trial-start-card-cta">
          <Sparkles className="h-4 w-4 mr-1" />
          Start Pro trial
        </Button>
      </div>
    </div>
  );
}
